import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientApi } from '../api';
import {
  FlaskConical, Search, ShoppingCart, Home, Clock, Droplets, Package,
  Star, Shield, MapPin, Phone, CreditCard, Banknote, CheckCircle2,
  ChevronRight, X, Plus, Minus, FileText,
} from 'lucide-react';

interface Service {
  id: string;
  category: string;
  subcategory: string | null;
  serviceName: string;
  serviceCode: string | null;
  description: string | null;
  basePrice: string;
  offerPrice: string | null;
  homeServiceAvailable: boolean;
  fastingRequired: boolean;
  tatMinutes: number | null;
  sampleType: string | null;
  isFeatured: boolean;
  prescriptionRequired: boolean;
}


export default function PatientLabStore() {
  const { labProfileId } = useParams<{ labProfileId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [homeOnly, setHomeOnly] = useState(false);
  const [cart, setCart] = useState<Service[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);

  // Real backend data: the provider record + the lab's actual test/package
  // catalogue (priced by the lab admin). Search/category filter client-side.
  const fetchStore = useCallback(async () => {
    if (!labProfileId) return;
    setLoading(true);
    try {
      const p: any = await patientApi.lab.getLabProfile(labProfileId).catch(() => null);
      if (p) {
        setProfile({
          displayName: p.name ?? 'Lab',
          verificationStatus: p.verified ? 'VERIFIED' : 'PENDING',
          address: p.address, city: p.city, phone: p.phone, description: p.description,
        });
        // No online payment integration — the only honest option is paying
        // the lab directly at the visit.
        setPaymentOptions({ allowCashAtVisit: true });
      }

      const [testsRes, pkgsRes] = await Promise.all([
        patientApi.lab.getLabTests(labProfileId).catch(() => []),
        patientApi.lab.getLabPackages(labProfileId).catch(() => []),
      ]);
      const toService = (row: any, category: string): Service => ({
        id: String(row.id), category, subcategory: null,
        serviceName: row.name ?? 'Test', serviceCode: null, description: null,
        basePrice: String(row.price ?? 0), offerPrice: null,
        homeServiceAvailable: false, fastingRequired: false,
        tatMinutes: null, sampleType: null, isFeatured: false, prescriptionRequired: false,
      });
      const all = [
        ...(Array.isArray(testsRes) ? testsRes : []).map((r: any) => toService(r, 'Test')),
        ...(Array.isArray(pkgsRes) ? pkgsRes : []).map((r: any) => toService(r, 'Package')),
      ];
      const q = search.trim().toLowerCase();
      setServices(all.filter((s) =>
        (!q || s.serviceName.toLowerCase().includes(q)) &&
        (!categoryFilter || s.category === categoryFilter) &&
        (!homeOnly || s.homeServiceAvailable)));
    } catch { /* handled by empty state */ }
    finally { setLoading(false); }
  }, [labProfileId, search, categoryFilter, homeOnly]);

  useEffect(() => { fetchStore(); }, [fetchStore]);

  useEffect(() => {
    if (services.length && !categories.length) {
      setCategories([...new Set(services.map((s) => s.category))]);
    }
  }, [services, categories.length]);

  const addToCart = (svc: Service) => {
    if (!cart.find((c) => c.id === svc.id)) setCart([...cart, svc]);
  };
  const removeFromCart = (id: string) => setCart(cart.filter((c) => c.id !== id));
  const cartTotal = cart.reduce((sum, c) => sum + Number(c.offerPrice || c.basePrice), 0);

  const featuredServices = services.filter((s) => s.isFeatured);

  if (loading && !profile) {
    return <div className="flex items-center justify-center py-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" /></div>;
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto py-20 text-center">
        <FlaskConical className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900">Lab Store Not Available</h2>
        <p className="text-gray-500 mt-2">This lab has not set up their online store yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Lab Header */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-teal-600 to-teal-800" />
        <div className="px-6 -mt-8 pb-4 flex items-end gap-4">
          <div className="w-16 h-16 rounded-xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
            <FlaskConical className="w-7 h-7 text-teal-600" />
          </div>
          <div className="pb-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{profile.displayName}</h1>
              {profile.verificationStatus === 'VERIFIED' && (
                <Shield className="w-4 h-4 text-green-500" />
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              {profile.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.city}</span>}
              {profile.publicPhone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{profile.publicPhone}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Featured */}
      {featuredServices.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Recommended Packages</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {featuredServices.slice(0, 6).map((s) => (
              <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-teal-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{s.serviceName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{s.category}</p>
                  </div>
                  <Package className="w-4 h-4 text-teal-600 shrink-0" />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-teal-700 font-bold">₹{Number(s.basePrice).toLocaleString()}</span>
                  <button
                    onClick={() => addToCart(s)}
                    disabled={!!cart.find((c) => c.id === s.id)}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500"
                  >
                    {cart.find((c) => c.id === s.id) ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {cart.find((c) => c.id === s.id) ? 'Added' : 'Add'}
                  </button>
                </div>
                <div className="flex gap-2 mt-2">
                  {s.homeServiceAvailable && <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">Home</span>}
                  {s.fastingRequired && <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Fasting</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text" placeholder="Search tests, packages..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => setHomeOnly(!homeOnly)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border ${homeOnly ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-gray-300 text-gray-600'}`}
          >
            <Home className="w-4 h-4" />
            Home Collection
          </button>
        </div>
      </div>

      {/* Service List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" /></div>
        ) : services.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FlaskConical className="w-12 h-12 mx-auto mb-3" />
            <p>No services found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {services.filter((s) => !s.isFeatured || categoryFilter || search).map((s) => (
              <div key={s.id} className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 text-sm truncate">{s.serviceName}</p>
                    {s.serviceCode && <span className="text-[10px] text-gray-400 font-mono shrink-0">{s.serviceCode}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-500">{s.category}</span>
                    {s.homeServiceAvailable && <span className="text-[10px] text-teal-600 flex items-center gap-0.5"><Home className="w-3 h-3" />Home</span>}
                    {s.fastingRequired && <span className="text-[10px] text-amber-600 flex items-center gap-0.5"><Droplets className="w-3 h-3" />Fasting</span>}
                    {s.tatMinutes && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />{Math.round(s.tatMinutes / 60)}h</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">₹{Number(s.basePrice).toLocaleString()}</p>
                    {s.offerPrice && <p className="text-xs text-green-600">₹{Number(s.offerPrice)}</p>}
                  </div>
                  <button
                    onClick={() => cart.find((c) => c.id === s.id) ? removeFromCart(s.id) : addToCart(s)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      cart.find((c) => c.id === s.id)
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
                    }`}
                  >
                    {cart.find((c) => c.id === s.id) ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Options */}
      {paymentOptions && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4 text-teal-600" /> Payment Options</h3>
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-1 bg-teal-50 text-teal-700 rounded-lg text-xs border border-teal-200">NeoHealthCard</span>
            {paymentOptions.allowDirectUpi && <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs border border-blue-200">UPI</span>}
            {paymentOptions.allowBankTransfer && <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs border border-purple-200">Bank Transfer</span>}
            {paymentOptions.allowCashAtVisit && <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs border border-amber-200">Cash</span>}
            {paymentOptions.allowCardAtCounter && <span className="px-2.5 py-1 bg-rose-50 text-rose-700 rounded-lg text-xs border border-rose-200">Card</span>}
          </div>
        </div>
      )}

      {/* Floating Cart */}
      {cart.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setShowCart(!showCart)}
            className="flex items-center gap-2 px-5 py-3 bg-teal-600 text-white rounded-xl shadow-xl hover:bg-teal-700 transition-colors"
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="font-medium">{cart.length} items</span>
            <span className="font-bold">₹{cartTotal.toLocaleString()}</span>
          </button>
        </div>
      )}

      {/* Cart Dialog */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setShowCart(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-teal-600" /> Booking Cart</h3>
              <button onClick={() => setShowCart(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-2">
              {cart.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{s.serviceName}</p>
                    <p className="text-xs text-gray-500">{s.category}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900">₹{Number(s.basePrice).toLocaleString()}</span>
                    <button onClick={() => removeFromCart(s.id)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Total Estimate</span>
                <span className="text-lg font-bold text-teal-700">₹{cartTotal.toLocaleString()}</span>
              </div>
              <button
                onClick={() => {
                  setShowCart(false);
                  navigate(`/patient/lab-booking/${labProfileId}`, {
                    state: { selectedServices: cart },
                  });
                }}
                className="w-full flex items-center justify-center gap-2 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium"
              >
                Proceed to Book
                <ChevronRight className="w-4 h-4" />
              </button>
              <label className="w-full flex items-center justify-center gap-2 py-2 text-gray-600 text-sm hover:text-gray-800 cursor-pointer">
                <FileText className="w-4 h-4" />
                Upload Prescription Instead
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setPrescriptionFile(file);
                  }}
                />
              </label>
              {prescriptionFile && (
                <div className="text-center space-y-1 pt-1">
                  <p className="text-xs text-teal-700 font-medium">Uploaded: {prescriptionFile.name}</p>
                  <p className="text-[10px] text-gray-400">Stored locally — will sync after backend integration</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
